/**
 * Word-level changes between a block's text and a rewrite proposal
 * (E14-design §5: "the proposal appears beside the current text with
 * changes highlighted"). Units are words, runs of white space and whole
 * `{{token}}` figures, so a token is never split. The longest common
 * subsequence of units is kept; the rest of the current text is
 * `removed`, the rest of the proposal `added`.
 */

export type DiffKind = 'same' | 'removed' | 'added';

export interface DiffPart {
  kind: DiffKind;
  text: string;
}

const UNIT = /\{\{[^{}]*\}\}|\s+|[^\s{]+|\{/g;

export function diffUnits(text: string): string[] {
  return text.match(UNIT) ?? [];
}

/** Past this many unit pairs the diff reports a whole replacement instead. */
const MAX_CELLS = 400_000;

/** Adjacent parts of one kind joined. */
function merged(parts: DiffPart[]): DiffPart[] {
  const out: DiffPart[] = [];
  for (const p of parts) {
    const last = out[out.length - 1];
    if (last && last.kind === p.kind) last.text += p.text;
    else out.push({ ...p });
  }
  return out;
}

export function diffWords(before: string, after: string): DiffPart[] {
  const a = diffUnits(before);
  const b = diffUnits(after);
  if (a.length * b.length > MAX_CELLS) {
    return merged([
      { kind: 'removed', text: before },
      { kind: 'added', text: after },
    ]).filter((p) => p.text.length > 0);
  }
  // lcs[i][j]: the common length of a[i..] and b[j..].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i]![j] =
        a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const parts: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      parts.push({ kind: 'same', text: a[i]! });
      i += 1;
      j += 1;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      parts.push({ kind: 'removed', text: a[i]! });
      i += 1;
    } else {
      parts.push({ kind: 'added', text: b[j]! });
      j += 1;
    }
  }
  while (i < a.length) parts.push({ kind: 'removed', text: a[i++]! });
  while (j < b.length) parts.push({ kind: 'added', text: b[j++]! });
  return merged(parts);
}

/** The parts the current text shows (kept and removed), or the proposal shows (kept and added). */
export function sideOf(parts: DiffPart[], side: 'before' | 'after'): DiffPart[] {
  const drop: DiffKind = side === 'before' ? 'added' : 'removed';
  return parts.filter((p) => p.kind !== drop);
}
